import {Icon} from '@wandb/weave/components/Icon';
import type {IconName} from '@wandb/weave/components/Icon/types';
import React, {useMemo} from 'react';

import {getSetActiveTagLink, type TagV2} from '../../../util/gallery/shared';
import {TOPIC_COLORS} from '../config';
import type {AuthorType} from '../types';
import * as S from './styles';

export type TopicFiltersProps = {
  activeAuthorType: AuthorType;
  onAuthorTypeChange: (type: AuthorType) => void;
  activeTag?: string;
  tagsV2: TagV2[];
};

const GROUP_ICONS: Record<string, IconName> = {
  'AI agent observability': 'view-glasses',
  'Build models': 'model',
  'About Weights & Biases': 'wandb',
};

const NAV_CATEGORIES = [
  'AI agent observability',
  'Build models',
  'About Weights & Biases',
];

const getTopicColor = (name: string, tagColor?: string) => {
  if (tagColor) {
    return {
      backgroundColor: tagColor,
      textColor: '#FFFFFF', // Default to white text for custom colors
    };
  }
  return TOPIC_COLORS[name] || TOPIC_COLORS['default'];
};

export const TopicFilters: React.FC<TopicFiltersProps> = ({
  activeAuthorType,
  onAuthorTypeChange,
  activeTag,
  tagsV2,
}) => {
  const tagsByCategory = useMemo(() => {
    const groups: Record<string, TagV2[]> = {};
    tagsV2.forEach(tag => {
      if (tag.navCategory) {
        if (!groups[tag.navCategory]) {
          groups[tag.navCategory] = [];
        }
        groups[tag.navCategory].push(tag);
      }
    });
    return groups;
  }, [tagsV2]);

  const authorTags = useMemo(() => {
    return tagsByCategory['Authors'] || [];
  }, [tagsByCategory]);

  return (
    <S.Container>
      {/* Authors Row */}
      <S.Row>
        <S.Label>
          <Icon name="user-author" style={{marginRight: 8}} />
          Author filters
        </S.Label>
        <S.PillsContainer>
          <S.PillButton
            $active={activeAuthorType === 'All'}
            $backgroundColor={TOPIC_COLORS['default'].backgroundColor}
            $textColor={TOPIC_COLORS['default'].textColor}
            onClick={() => onAuthorTypeChange('All')}>
            All
          </S.PillButton>
          {authorTags.map(tag => {
            const colors = getTopicColor(tag.name, tag.color);
            return (
              <S.PillButton
                key={tag.id}
                $active={activeAuthorType === tag.name}
                $backgroundColor={colors.backgroundColor}
                $textColor={colors.textColor}
                onClick={() => onAuthorTypeChange(tag.name)}>
                {tag.name}
              </S.PillButton>
            );
          })}
        </S.PillsContainer>
      </S.Row>

      <S.Separator />

      {/* Topic Rows */}
      {NAV_CATEGORIES.map(categoryName => {
        const categoryTags = tagsByCategory[categoryName] || [];
        if (categoryTags.length === 0) {
          return null;
        }

        return (
          <S.Row key={categoryName}>
            <S.Label>
              <Icon
                name={GROUP_ICONS[categoryName] || 'tag'}
                style={{marginRight: 8}}
              />
              {categoryName}
            </S.Label>
            <S.PillsContainer>
              {categoryTags.map(tag => {
                const isExternal = tag.linkForm
                  .toLowerCase()
                  .startsWith('http');
                const isActive = activeTag === tag.name;
                const colors = getTopicColor(tag.name, tag.color);

                if (isExternal) {
                  return (
                    <S.ExternalPillLink
                      key={tag.id}
                      href={tag.linkForm}
                      target="_blank"
                      rel="noopener noreferrer"
                      $backgroundColor={colors.backgroundColor}
                      $textColor={colors.textColor}>
                      {tag.name}
                    </S.ExternalPillLink>
                  );
                }

                return (
                  <S.PillLink
                    key={tag.id}
                    to={getSetActiveTagLink(tag.linkForm)}
                    $active={isActive}
                    $backgroundColor={colors.backgroundColor}
                    $textColor={colors.textColor}>
                    {tag.name}
                  </S.PillLink>
                );
              })}
            </S.PillsContainer>
          </S.Row>
        );
      })}
    </S.Container>
  );
};
